import { useMemo, useRef, useState } from 'react';
import type { TimeSeriesPoint } from '@ssn/shared';
import { ChartEmpty, seriesColour, type Series } from './ChartFrame';

const PADDING = { top: 12, right: 16, bottom: 26, left: 40 } as const;

export interface LineChartProps {
  points: readonly TimeSeriesPoint[];
  series: readonly Series[];
  height?: number;
  /** Formats a value for the tooltip and the axis, e.g. hours or a plain count. */
  formatValue?: (value: number) => string;
  /** Fill the area under a single series. Only meaningful for one series. */
  area?: boolean;
}

/**
 * A time-series line chart.
 *
 * Hand-drawn SVG rather than a charting library: it is a small amount of code,
 * it inherits the theme tokens directly (so dark mode is not an afterthought),
 * and it keeps the marks exactly to spec — 2px lines, 8px end markers with a
 * surface ring, hairline gridlines and a shared crosshair tooltip.
 */
export function LineChart({ points, series, height = 200, formatValue, area = false }: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const format = formatValue ?? ((value: number) => String(Math.round(value)));

  const scale = useMemo(() => {
    const values = points.flatMap((point) => series.map((entry) => point.values[entry.key] ?? 0));
    const max = Math.max(1, ...values);
    // Round the top of the axis up to a clean number so ticks read 0 / 5 / 10.
    const magnitude = 10 ** Math.floor(Math.log10(max));
    const niceMax = Math.ceil(max / magnitude) * magnitude;
    return { max: niceMax };
  }, [points, series]);

  if (points.length === 0) return <ChartEmpty />;

  const width = 720;
  const plotWidth = width - PADDING.left - PADDING.right;
  const plotHeight = height - PADDING.top - PADDING.bottom;

  const x = (index: number): number =>
    points.length === 1
      ? PADDING.left + plotWidth / 2
      : PADDING.left + (index / (points.length - 1)) * plotWidth;

  const y = (value: number): number => PADDING.top + plotHeight - (value / scale.max) * plotHeight;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
    value: scale.max * fraction,
    y: PADDING.top + plotHeight - fraction * plotHeight,
  }));

  // Enough labels to orient the reader, never so many that they collide.
  const labelStep = Math.max(1, Math.ceil(points.length / 7));

  const onMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relative = ((event.clientX - rect.left) / rect.width) * width;
    const ratio = (relative - PADDING.left) / plotWidth;
    const index = Math.round(ratio * (points.length - 1));
    setHover(index >= 0 && index < points.length ? index : null);
  };

  const active = hover !== null ? points[hover] : undefined;

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ height }}
        role="img"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={tick.y}
              y2={tick.y}
              stroke="var(--grid-line)"
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 8}
              y={tick.y + 3.5}
              textAnchor="end"
              className="fill-[var(--text-subtle)] text-[10px] tabular"
            >
              {format(tick.value)}
            </text>
          </g>
        ))}

        {points.map((point, index) =>
          index % labelStep === 0 ? (
            <text
              key={point.bucket}
              x={x(index)}
              y={height - 8}
              textAnchor="middle"
              className="fill-[var(--text-subtle)] text-[10px]"
            >
              {point.label}
            </text>
          ) : null,
        )}

        {area && series[0] && (
          <path
            d={`${points
              .map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(index)} ${y(point.values[series[0]!.key] ?? 0)}`)
              .join(' ')} L ${x(points.length - 1)} ${PADDING.top + plotHeight} L ${x(0)} ${
              PADDING.top + plotHeight
            } Z`}
            fill={seriesColour(series[0].slot)}
            fillOpacity={0.1}
          />
        )}

        {series.map((entry) => (
          <path
            key={entry.key}
            d={points
              .map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(index)} ${y(point.values[entry.key] ?? 0)}`)
              .join(' ')}
            fill="none"
            stroke={seriesColour(entry.slot)}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* The last point of each series is marked, so a flat line still reads. */}
        {series.map((entry) => {
          const lastIndex = points.length - 1;
          const point = points[lastIndex];
          if (!point) return null;
          return (
            <circle
              key={`end-${entry.key}`}
              cx={x(lastIndex)}
              cy={y(point.values[entry.key] ?? 0)}
              r={4}
              fill={seriesColour(entry.slot)}
              stroke="var(--surface)"
              strokeWidth={2}
            />
          );
        })}

        {hover !== null && active && (
          <g>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PADDING.top}
              y2={PADDING.top + plotHeight}
              stroke="var(--border-strong)"
              strokeWidth={1}
            />
            {series.map((entry) => (
              <circle
                key={`hover-${entry.key}`}
                cx={x(hover)}
                cy={y(active.values[entry.key] ?? 0)}
                r={4.5}
                fill={seriesColour(entry.slot)}
                stroke="var(--surface)"
                strokeWidth={2}
              />
            ))}
          </g>
        )}
      </svg>

      {hover !== null && active && (
        <div
          role="status"
          className="pointer-events-none absolute top-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs shadow-panel"
          style={{
            left: `${Math.min(78, Math.max(2, (x(hover) / width) * 100))}%`,
          }}
        >
          <p className="font-medium text-text">{active.label}</p>
          <ul className="mt-1 space-y-0.5">
            {series.map((entry) => (
              <li key={entry.key} className="flex items-center gap-2 whitespace-nowrap">
                <span
                  aria-hidden
                  className="inline-block size-2 rounded-full"
                  style={{ backgroundColor: seriesColour(entry.slot) }}
                />
                <span className="text-text-muted">{entry.label}</span>
                <span className="ml-auto font-medium text-text tabular">
                  {format(active.values[entry.key] ?? 0)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
